import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { useI18n } from "../lib/i18n";
import { Trash2, Plus, Wand2, Loader2 } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { computeTotals, formatCurrency, todayISO, addDaysISO } from "../lib/format";
import { toast } from "sonner";

const EMPTY_ITEM = { description: "", quantity: 1, unit_price: 0, vat_rate: 21, irpf_rate: 0, discount: 0 };

export default function DocumentEditor({
  kind,                       // 'quote' or 'invoice'
  existing,                   // existing document or null
  clients,
  onSaved,
  statusOptions,              // ['draft','sent',...]
}) {
  const { t, lang } = useI18n();
  const [form, setForm] = useState(() => buildInitial(kind, existing));
  const [busy, setBusy] = useState(false);
  const [improvingIdx, setImprovingIdx] = useState(-1);

  useEffect(() => {
    setForm(buildInitial(kind, existing));
  }, [kind, existing?.id]); // eslint-disable-line

  // OCR prefill: when navigating from OCR dialog, pre-populate the form
  useEffect(() => {
    if (existing) return; // only for new documents
    try {
      const raw = sessionStorage.getItem("hosys_ocr_prefill");
      if (!raw) return;
      const payload = JSON.parse(raw);
      if (payload.kind !== kind) return;
      const d = payload.data || {};
      // Try to match a client by name or NIF
      let matchedClientId = "";
      if (clients && clients.length) {
        const target = (d.client_name || "").trim().toLowerCase();
        const nif = (d.client_nif || "").trim().toUpperCase();
        const match = clients.find(
          (c) =>
            (target && c.name?.toLowerCase().includes(target)) ||
            (nif && c.nif?.toUpperCase() === nif)
        );
        if (match) matchedClientId = match.id;
      }
      setForm((f) => ({
        ...f,
        client_id: matchedClientId || f.client_id,
        date: d.date || f.date,
        items:
          Array.isArray(d.items) && d.items.length
            ? d.items.map((it) => ({
                description: it.description || "",
                quantity: Number(it.quantity || 1),
                unit_price: Number(it.unit_price || 0),
                vat_rate: Number(it.vat_rate ?? 21),
                irpf_rate: Number(it.irpf_rate || 0),
                discount: Number(it.discount || 0),
              }))
            : f.items,
        notes: d.notes ? `${d.notes}` : f.notes,
      }));
      sessionStorage.removeItem("hosys_ocr_prefill");
      const missing = !matchedClientId && d.client_name;
      if (missing) {
        toast.info(
          lang === "es"
            ? `Cliente "${d.client_name}" no encontrado. Selecciónalo o créalo antes de guardar.`
            : `Client "${d.client_name}" no trobat. Selecciona'l o crea'l abans de desar.`,
          { duration: 6000 }
        );
      } else {
        toast.success(lang === "es" ? "Datos OCR aplicados" : "Dades OCR aplicades");
      }
    } catch (e) {
      console.warn("OCR prefill failed:", e);
    }
  }, [existing, clients, kind, lang]);

  const updateField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const updateItem = (i, k, v) => {
    setForm((f) => {
      const items = [...f.items];
      items[i] = { ...items[i], [k]: k === "description" ? v : Number(v) };
      return { ...f, items };
    });
  };

  const addItem = () =>
    setForm((f) => ({ ...f, items: [...f.items, { ...EMPTY_ITEM }] }));

  const removeItem = (i) =>
    setForm((f) => ({ ...f, items: f.items.filter((_, idx) => idx !== i) }));

  const totals = computeTotals(form.items);

  const improve = async (i) => {
    setImprovingIdx(i);
    try {
      const r = await api.post("/ai/improve-description", {
        text: form.items[i].description,
        language: lang,
      });
      updateItem(i, "description", r.data.text);
    } catch (e) {
      toast.error("Error IA");
    } finally {
      setImprovingIdx(-1);
    }
  };

  const save = async () => {
    if (!form.client_id) {
      toast.error(t("editor.select_client"));
      return;
    }
    setBusy(true);
    try {
      const payload = { ...form };
      // Strip unused fields
      const url = kind === "quote" ? "/quotes" : "/invoices";
      let r;
      if (existing) r = await api.put(`${url}/${existing.id}`, payload);
      else r = await api.post(url, payload);
      toast.success(t("settings.saved"));
      onSaved?.(r.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Error");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="grid lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2 space-y-6 min-w-0">
        {/* Header card */}
        <div className="card-surface p-6 space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="text-xs uppercase tracking-wider text-slate-400 block mb-2">
                {t("editor.client")} *
              </label>
              <Select value={form.client_id} onValueChange={(v) => updateField("client_id", v)}>
                <SelectTrigger className="bg-[#05070D] border-white/10" data-testid="editor-client-select">
                  <SelectValue placeholder={t("editor.select_client")} />
                </SelectTrigger>
                <SelectContent className="bg-[#0B0F19] border-white/10">
                  {clients.map((c) => (
                    <SelectItem key={c.id} value={c.id} data-testid={`client-option-${c.id}`}>
                      {c.name} {c.nif_cif ? `· ${c.nif_cif}` : ""}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <label className="text-xs uppercase tracking-wider text-slate-400 block mb-2">
                {t("editor.date")}
              </label>
              <Input
                type="date"
                value={form.date}
                onChange={(e) => updateField("date", e.target.value)}
                className="bg-[#05070D] border-white/10"
                data-testid="editor-date"
              />
            </div>
            {kind === "quote" && (
              <div>
                <label className="text-xs uppercase tracking-wider text-slate-400 block mb-2">
                  {t("quotes.valid_until")}
                </label>
                <Input
                  type="date"
                  value={form.valid_until || ""}
                  onChange={(e) => updateField("valid_until", e.target.value)}
                  className="bg-[#05070D] border-white/10"
                />
              </div>
            )}
            {kind === "invoice" && (
              <div>
                <label className="text-xs uppercase tracking-wider text-slate-400 block mb-2">
                  {t("editor.due_date")}
                </label>
                <Input
                  type="date"
                  value={form.due_date || ""}
                  onChange={(e) => updateField("due_date", e.target.value)}
                  className="bg-[#05070D] border-white/10"
                  data-testid="editor-due-date"
                />
              </div>
            )}
            <div>
              <label className="text-xs uppercase tracking-wider text-slate-400 block mb-2">
                {t("editor.status")}
              </label>
              <Select value={form.status} onValueChange={(v) => updateField("status", v)}>
                <SelectTrigger className="bg-[#05070D] border-white/10" data-testid="editor-status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#0B0F19] border-white/10">
                  {statusOptions.map((s) => (
                    <SelectItem key={s} value={s}>{t(`status.${s}`, s)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {/* Items */}
        <div className="card-surface p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-display text-lg font-semibold">Línies</h3>
            <Button
              onClick={addItem}
              variant="outline"
              size="sm"
              data-testid="editor-add-item"
              className="border-blue-500/30 text-blue-300 hover:bg-blue-500/10"
            >
              <Plus size={14} className="mr-1" />
              {t("editor.add_item")}
            </Button>
          </div>
          <div className="space-y-3">
            {form.items.map((it, i) => (
              <div key={i} className="border border-white/5 rounded-lg p-4 bg-white/[0.02]" data-testid={`editor-item-${i}`}>
                <div className="space-y-3">
                  <div className="flex items-start gap-2">
                    <Textarea
                      value={it.description}
                      onChange={(e) => updateItem(i, "description", e.target.value)}
                      placeholder={t("editor.description")}
                      className="bg-[#05070D] border-white/10 min-h-[60px] flex-1"
                      data-testid={`editor-item-desc-${i}`}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => improve(i)}
                      disabled={!it.description || improvingIdx === i}
                      className="border-blue-500/30 text-blue-300 hover:bg-blue-500/10"
                      data-testid={`editor-item-improve-${i}`}
                    >
                      {improvingIdx === i ? <Loader2 size={14} className="animate-spin" /> : <Wand2 size={14} />}
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => removeItem(i)}
                      className="border-rose-500/30 text-rose-300 hover:bg-rose-500/10"
                      data-testid={`editor-item-remove-${i}`}
                    >
                      <Trash2 size={14} />
                    </Button>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-5 gap-2">
                    <NumInput label={t("editor.quantity")} value={it.quantity} onChange={(v) => updateItem(i, "quantity", v)} testid={`item-qty-${i}`} />
                    <NumInput label={t("editor.unit_price")} value={it.unit_price} onChange={(v) => updateItem(i, "unit_price", v)} testid={`item-price-${i}`} />
                    <NumInput label={t("editor.vat")} value={it.vat_rate} onChange={(v) => updateItem(i, "vat_rate", v)} testid={`item-vat-${i}`} />
                    <NumInput label={t("editor.irpf")} value={it.irpf_rate} onChange={(v) => updateItem(i, "irpf_rate", v)} />
                    <NumInput label={t("editor.discount")} value={it.discount} onChange={(v) => updateItem(i, "discount", v)} />
                  </div>
                  <div className="flex items-center justify-end text-sm text-slate-300">
                    {formatCurrency(
                      Number(it.quantity || 0) * Number(it.unit_price || 0) * (1 - Number(it.discount || 0) / 100),
                      lang,
                    )}
                  </div>
                </div>
              </div>
            ))}
            {form.items.length === 0 && (
              <div className="text-center text-slate-500 py-8 border border-dashed border-white/10 rounded-lg">
                Encara no hi ha línies. Afegeix-ne una!
              </div>
            )}
          </div>
        </div>

        {/* Notes */}
        <div className="card-surface p-6 grid sm:grid-cols-2 gap-4">
          <div>
            <label className="text-xs uppercase tracking-wider text-slate-400 block mb-2">{t("editor.notes")}</label>
            <Textarea value={form.notes || ""} onChange={(e) => updateField("notes", e.target.value)} className="bg-[#05070D] border-white/10 min-h-[100px]" />
          </div>
          <div>
            <label className="text-xs uppercase tracking-wider text-slate-400 block mb-2">{t("editor.observations")}</label>
            <Textarea value={form.observations || ""} onChange={(e) => updateField("observations", e.target.value)} className="bg-[#05070D] border-white/10 min-h-[100px]" />
          </div>
        </div>
      </div>

      {/* Totals sidebar */}
      <div className="space-y-4">
        <div className="card-surface p-6 lg:sticky lg:top-20">
          <h3 className="font-display text-lg font-semibold mb-4">Totals</h3>
          <Row label={t("editor.subtotal")} value={formatCurrency(totals.subtotal, lang)} />
          {totals.discount_total > 0 && (
            <Row label={t("editor.discount")} value={`-${formatCurrency(totals.discount_total, lang)}`} />
          )}
          <Row label={t("editor.vat")} value={formatCurrency(totals.tax_total, lang)} />
          {totals.irpf_total > 0 && (
            <Row label={t("editor.irpf")} value={`-${formatCurrency(totals.irpf_total, lang)}`} />
          )}
          <div className="my-3 border-t border-white/10" />
          <div className="flex items-center justify-between">
            <span className="text-sm text-slate-300">{t("editor.total")}</span>
            <span className="font-display text-2xl font-bold gradient-text">
              {formatCurrency(totals.total, lang)}
            </span>
          </div>
          <Button
            onClick={save}
            disabled={busy}
            className="w-full mt-6 bg-gradient-to-r from-blue-600 to-blue-500 hover:from-blue-500 hover:to-blue-400 shadow-[0_0_20px_rgba(59,130,246,0.3)]"
            data-testid="editor-save-btn"
          >
            {busy ? <Loader2 size={16} className="animate-spin mr-2" /> : null}
            {busy ? t("editor.saving") : t("editor.save")}
          </Button>
        </div>
      </div>
    </div>
  );
}

const Row = ({ label, value }) => (
  <div className="flex items-center justify-between py-1.5 text-sm">
    <span className="text-slate-400">{label}</span>
    <span className="text-slate-200">{value}</span>
  </div>
);

const NumInput = ({ label, value, onChange, testid }) => (
  <div>
    <label className="text-[10px] uppercase tracking-wider text-slate-500 block mb-1">{label}</label>
    <Input
      type="number"
      step="0.01"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="bg-[#05070D] border-white/10 h-9 text-sm"
      data-testid={testid}
    />
  </div>
);

function buildInitial(kind, existing) {
  if (existing) {
    return {
      client_id: existing.client_id || "",
      date: existing.date || todayISO(),
      items: existing.items?.length
        ? existing.items.map((i) => ({
            description: i.description || "",
            quantity: Number(i.quantity || 1),
            unit_price: Number(i.unit_price || 0),
            vat_rate: Number(i.vat_rate || 21),
            irpf_rate: Number(i.irpf_rate || 0),
            discount: Number(i.discount || 0),
          }))
        : [],
      notes: existing.notes || "",
      observations: existing.observations || "",
      status: existing.status || (kind === "quote" ? "draft" : "pending"),
      valid_until: existing.valid_until || (kind === "quote" ? addDaysISO(30) : undefined),
      due_date: existing.due_date || (kind === "invoice" ? addDaysISO(30) : undefined),
      kind: existing.kind || (kind === "invoice" ? "complete" : undefined),
    };
  }
  return {
    client_id: "",
    date: todayISO(),
    items: [{ ...EMPTY_ITEM }],
    notes: "",
    observations: "",
    status: kind === "quote" ? "draft" : "pending",
    valid_until: kind === "quote" ? addDaysISO(30) : undefined,
    due_date: kind === "invoice" ? addDaysISO(30) : undefined,
    kind: kind === "invoice" ? "complete" : undefined,
  };
}
